"use client";

import * as Sentry from "@sentry/nextjs";
import { useTranslations } from "next-intl";
import {
  Component,
  type ErrorInfo,
  type ReactNode,
  useCallback,
  useState,
} from "react";

import { toast } from "@/lib/toast";

/**
 * Inner class component — React requires class components for Error Boundaries.
 * Wrapped by the functional ClientErrorBoundary below to provide translations.
 */
class ErrorBoundaryInner extends Component<
  {
    children: ReactNode;
    fallback: ReactNode;
    toastMessage: string;
  },
  { hasError: boolean }
> {
  constructor(
    props: Readonly<{
      children: ReactNode;
      fallback: ReactNode;
      toastMessage: string;
    }>
  ) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
    console.error(
      `ClientErrorBoundary caught: ${error.message}\n${errorInfo.componentStack ?? ""}`
    );
    toast.error(this.props.toastMessage);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

function DefaultFallback({ onReset }: { onReset: () => void }) {
  const t = useTranslations("errorBoundary");

  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-red-200 bg-red-50 p-6 text-center dark:border-red-900/50 dark:bg-red-950/30">
      <p className="text-sm font-medium text-red-800 dark:text-red-200">
        {t("componentTitle")}
      </p>
      <p className="text-xs text-red-600 dark:text-red-400">
        {t("componentDescription")}
      </p>
      <button
        // The section may sit inside a form; a Retry click must never submit it.
        type="button"
        onClick={onReset}
        className="rounded-md bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200"
      >
        {t("retry")}
      </button>
    </div>
  );
}

/**
 * Catches render errors in its children, shows a toast, and renders a fallback.
 *
 * Usage:
 *   <ClientErrorBoundary>
 *     <RiskyComponent />
 *   </ClientErrorBoundary>
 *
 * With custom fallback:
 *   <ClientErrorBoundary fallback={<p>Custom error UI</p>}>
 *     <RiskyComponent />
 *   </ClientErrorBoundary>
 *
 * An explicit null fallback renders nothing after a crash (the toast,
 * Sentry capture and console.error still fire). Reserve it for chrome whose
 * absence the user won't miss, such as the exchange progress bar; omit
 * `fallback` everywhere else so the user gets the card with Retry.
 */
export function ClientErrorBoundary({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const t = useTranslations("errorBoundary");
  const [resetKey, setResetKey] = useState(0);

  const handleReset = useCallback(() => {
    setResetKey((k) => k + 1);
  }, []);

  return (
    <ErrorBoundaryInner
      key={resetKey}
      toastMessage={t("toast")}
      fallback={
        fallback === undefined ? (
          <DefaultFallback onReset={handleReset} />
        ) : (
          fallback
        )
      }
    >
      {children}
    </ErrorBoundaryInner>
  );
}
